import React, { useMemo } from 'react';
import classNames from 'classnames';
import { ProTable, type ProColumns, type ProTableProps } from '@ant-design/pro-components';
import {
  useAntdResizableHeader,
  type ResizableColumnsType,
} from 'use-antd-resizable-header';
import 'use-antd-resizable-header/dist/style.css';
import './index.less';

export type ResizableProColumns<T extends Record<string, any>> = ResizableColumnsType<
  ProColumns<T>[]
>;

export type ResizableProTableProps<
  T extends Record<string, any>,
  U extends Record<string, any> = Record<string, any>,
> = Omit<ProTableProps<T, U>, 'columns' | 'components' | 'scroll'> & {
  columns: ResizableProColumns<T>;
  /** 与列定义 useMemo 的依赖项保持一致，避免拖拽时重复渲染 */
  columnMemoDeps?: React.DependencyList;
  /** localStorage key，用于持久化列宽 */
  columnWidthPersistenceKey?: string;
  minConstraints?: number;
  scroll?: ProTableProps<T, U>['scroll'];
};

function ResizableProTable<
  T extends Record<string, any>,
  U extends Record<string, any> = Record<string, any>,
>({
  columns,
  columnMemoDeps = [],
  columnWidthPersistenceKey,
  minConstraints = 80,
  scroll: scrollProp,
  className,
  ...rest
}: ResizableProTableProps<T, U>) {
  const memoizedColumns = useMemo(
    () => columns,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    columnMemoDeps.length > 0 ? columnMemoDeps : [columns],
  );

  const { components, resizableColumns, tableWidth } = useAntdResizableHeader({
    columns: memoizedColumns,
    minConstraints,
    columnsState: columnWidthPersistenceKey
      ? {
          persistenceKey: columnWidthPersistenceKey,
          persistenceType: 'localStorage',
        }
      : undefined,
  });

  const scroll = useMemo(() => {
    if (scrollProp && typeof scrollProp === 'object') {
      return { ...scrollProp, x: tableWidth };
    }
    return { x: tableWidth };
  }, [scrollProp, tableWidth]);

  return (
    <ProTable<T, U>
      {...rest}
      className={classNames('resizable-pro-table', className)}
      columns={resizableColumns as ProColumns<T>[]}
      components={components}
      scroll={scroll}
    />
  );
}

export default ResizableProTable;
